import { isImmutableId } from './isImmutableId';
import { default as translateEwsImmutableIdsToEwsIds } from '../translators/translateEwsImmutableIdsToEwsIds';
import { default as translateEwsIdsToEwsImmutableIds } from '../translators/translateEwsIdsToEwsImmutableIds';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isConsumer } from 'owa-session-store';
import { createRestApiRequestHeaders } from 'owa-headers';
import { isConnectedAccount, getDefaultConnectedAccount } from 'owa-accounts-store';
import { logUsage } from 'owa-analytics';
import type { convertIdSource } from '../schema';

const enterpriseFlight = 'fwk-immutable-ids';
const consumerFlight = 'fwk-immutable-ids-consumer';

// NOTE: this conversion might change order of calendar ids in the event input id string has mixed immutables and
// ews item ids
export async function convertIdsToExpectedFormat(
    ids: string[],
    userIdentity: string,
    source: convertIdSource,
    combinedArray?: boolean
) {
    const isConsumerAccount = isConsumer(userIdentity);
    // check which ids in the string are ews item id and which are ews immutable
    const inputImmutableIds: string[] = [];
    const inputEwsIds: string[] = [];
    ids?.forEach(id => {
        isImmutableId(id) ? inputImmutableIds.push(id) : inputEwsIds.push(id);
    });

    let convertedIds: string[] = ids;
    let didConvert = false;

    const flightNameToCheck = isConsumerAccount ? consumerFlight : enterpriseFlight;

    // if we don't know userIdentity than we need convert all ids individually and check whether translations are null
    if (combinedArray) {
        if (isFeatureEnabled(enterpriseFlight) && inputEwsIds.length > 0) {
            convertedIds = await convertCombinedEwsIdsToEwsImmutableIds(
                inputEwsIds,
                inputImmutableIds
            );
            didConvert = true;
        } else if (!isFeatureEnabled(enterpriseFlight) && inputImmutableIds.length > 0) {
            convertedIds = await convertCombinedImmutableIdsToEwsIds(
                inputImmutableIds,
                inputEwsIds
            );
            didConvert = true;
        }
    }
    // else check per account in group based on consumer/enterprise flight
    else {
        if (isFeatureEnabled(flightNameToCheck) && inputEwsIds.length > 0) {
            convertedIds = await convertEwsIdsToEwsImmutableIds(
                inputEwsIds,
                inputImmutableIds,
                userIdentity
            );
            didConvert = true;
        } else if (!isFeatureEnabled(flightNameToCheck) && inputImmutableIds.length > 0) {
            convertedIds = await convertEwsImmutableIdsToEwsIds(
                inputImmutableIds,
                inputEwsIds,
                userIdentity
            );
            didConvert = true;
        }
    }
    if (didConvert) {
        logUsage('immutableIdConversion', {
            convertedType_1: isImmutableId(convertedIds[0]) ? 'immutable' : 'ews',
            isConsumer_2: isConsumerAccount,
            flightEnabled_3: isFeatureEnabled(flightNameToCheck),
            source_4: source,
        });
    }
    return {
        ids: convertedIds,
        didConvert: didConvert,
    };
}

async function convertEwsImmutableIdsToEwsIds(
    immutableIds: string[],
    ewsIds: string[],
    userIdentity: string
) {
    const headers = isConnectedAccount(userIdentity)
        ? await createRestApiRequestHeaders(userIdentity)
        : undefined;
    const convertedResponse = await translateEwsImmutableIdsToEwsIds(immutableIds, headers);
    // ews immutable id to ews item id translation returns array of response objects not string id array
    const convertedEwsIds = convertedResponse.map(id => id.TargetId);
    // return concatenated array of converted ews ids and any existing ews ids in input array
    return convertedEwsIds.concat(ewsIds);
}

async function convertEwsIdsToEwsImmutableIds(
    ewsIds: string[],
    immutableIds: string[],
    userIdentity: string
) {
    const headers = isConnectedAccount(userIdentity)
        ? await createRestApiRequestHeaders(userIdentity)
        : undefined;
    const convertedImmutableIds = await translateEwsIdsToEwsImmutableIds(ewsIds, headers);
    // return concatenated array of converted immutable ids and any existing immutable ids in input array
    return convertedImmutableIds.concat(immutableIds);
}

async function convertCombinedEwsIdsToEwsImmutableIds(ewsIds: string[], immutableIds: string[]) {
    const convertedImmutableIds: string[] = [];
    for (const ewsId of ewsIds) {
        // try to convert each ewsId individually with default mailbox
        const convertedImmutableId = await translateEwsIdsToEwsImmutableIds(
            [ewsId],
            undefined // headers
        );
        // if translation exists, push to converted output array
        if (convertedImmutableId[0]) {
            convertedImmutableIds.push(convertedImmutableId[0]);
        } else {
            // if translation is null, check if there's a connected account in accounts store and try that mailbox
            const connectedAccount = getDefaultConnectedAccount();
            if (isFeatureEnabled(consumerFlight) && connectedAccount) {
                const headers = await createRestApiRequestHeaders(connectedAccount.userIdentity);
                const consumerConvertedImmutableId = await translateEwsIdsToEwsImmutableIds(
                    [ewsId],
                    headers
                )[0];
                if (consumerConvertedImmutableId) {
                    convertedImmutableIds.push(consumerConvertedImmutableId);
                }
                // if translation is still null, we don't need any translation on this id, just push it to output
                else {
                    convertedImmutableIds.push(ewsId);
                }
            } else {
                convertedImmutableIds.push(ewsId);
            }
        }
    }
    // return concatenated array of converted immutable ids and any existing immutable ids in input array
    return convertedImmutableIds.concat(immutableIds);
}

async function convertCombinedImmutableIdsToEwsIds(immutableIds: string[], ewsIds: string[]) {
    const convertedEwsIds: string[] = [];
    for (const immutableId of immutableIds) {
        // try to convert each immutableId individually with default mailbox
        const convertedEwsId = await translateEwsImmutableIdsToEwsIds(
            [immutableId],
            undefined // headers
        );
        // if translation exists, push to converted output array
        if (convertedEwsId[0].TargetId) {
            convertedEwsIds.push(convertedEwsId[0].TargetId);
        } else {
            // if translation is null, check if there's a connected account in accounts store and try that mailbox
            // convertedEwsIds.push(immutableId);
            const connectedAccount = getDefaultConnectedAccount();
            if (isFeatureEnabled(consumerFlight) && connectedAccount) {
                const headers = await createRestApiRequestHeaders(connectedAccount.userIdentity);
                const consumerConvertedEwsId = await translateEwsImmutableIdsToEwsIds(
                    [immutableId],
                    headers
                )[0];
                if (consumerConvertedEwsId) {
                    convertedEwsIds.push(consumerConvertedEwsId.TargetId);
                } else {
                    convertedEwsIds.push(immutableId);
                }
            } else {
                convertedEwsIds.push(immutableId);
            }
        }
    }
    // return concatenated array of converted ews ids and any existing ews ids in input array
    return convertedEwsIds.concat(ewsIds);
}
